import React, { useMemo, useState } from 'react';
import { Table, TableHead, TableBody, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import QueryString from 'qs';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import { getSmsBranchBalances } from '../../../api/sms';
import SmsBalanceAdjustModal from './SmsBalanceAdjustModal';
import {
  TableControlsRow,
  PageSizeGroup,
  PageSizeLabel,
  PageSizeSelect,
  SearchGroup,
  SearchInput,
  SearchButton,
  HeaderCell,
  BodyCell,
  DataRow,
  BalanceText,
  ActionGroup,
  GrantButton,
  RevokeButton,
  PaginationContainer,
  PageButton,
  NavButton,
} from './SmsBalanceAdjustStyled';

const COLUMNS = [
  { id: 'branchName', label: '지점명', width: 240 },
  { id: 'balance', label: '잔여 건수', width: 140 },
  { id: 'totalCharged', label: '총 충전', width: 140 },
  { id: 'totalUsed', label: '총 사용', width: 140 },
  { id: 'action', label: '조정', width: 180 },
];

const formatCount = value => (value ?? 0).toLocaleString();

const SmsBalanceAdjust = () => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state.user);
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchValue, setSearchValue] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [adjustTarget, setAdjustTarget] = useState(null);

  const query = QueryString.stringify({ keyword: searchKeyword || undefined }, { skipNulls: true });

  const { data: balanceData } = useQuery({
    queryKey: ['smsBranchBalances', searchKeyword],
    queryFn: () => getSmsBranchBalances(query, user?.token),
    enabled: !!user?.token,
  });

  // 응답이 전체 목록이라 페이징은 화면에서 자른다.
  const branches = useMemo(() => balanceData || [], [balanceData]);
  const totalPages = Math.max(1, Math.ceil(branches.length / pageSize));
  const pagedBranches = useMemo(
    () => branches.slice((page - 1) * pageSize, page * pageSize),
    [branches, page, pageSize],
  );

  const handleSearch = () => {
    setSearchKeyword(searchValue);
    setPage(1);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleAdjustSuccess = () => {
    setAdjustTarget(null);
    queryClient.invalidateQueries({ queryKey: ['smsBranchBalances'] });
  };

  const renderCellValue = (branch, columnId) => {
    switch (columnId) {
      case 'branchName':
        return branch.branchName || '-';
      case 'balance':
        return <BalanceText>{formatCount(branch.balance)}</BalanceText>;
      case 'totalCharged':
        return formatCount(branch.totalCharged);
      case 'totalUsed':
        return formatCount(branch.totalUsed);
      case 'action':
        return (
          <ActionGroup>
            <GrantButton onClick={() => setAdjustTarget({ mode: 'grant', branch })}>{t('지급')}</GrantButton>
            <RevokeButton onClick={() => setAdjustTarget({ mode: 'revoke', branch })} disabled={!branch.balance}>
              {t('회수')}
            </RevokeButton>
          </ActionGroup>
        );
      default:
        return '-';
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    const endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <PaginationContainer>
        <NavButton onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={page === 1}>
          <KeyboardArrowLeftIcon />
        </NavButton>
        {pages.map(p => (
          <PageButton key={p} isActive={p === page} onClick={() => setPage(p)}>
            {p}
          </PageButton>
        ))}
        {endPage < totalPages && (
          <>
            <PageButton disabled>...</PageButton>
            <PageButton onClick={() => setPage(totalPages)}>{totalPages}</PageButton>
          </>
        )}
        <NavButton onClick={() => setPage(prev => Math.min(totalPages, prev + 1))} disabled={page === totalPages}>
          <KeyboardArrowRightIcon />
        </NavButton>
      </PaginationContainer>
    );
  };

  return (
    <>
      <TableControlsRow>
        <PageSizeGroup>
          <PageSizeLabel>{t('페이지당 항목 수')}</PageSizeLabel>
          <PageSizeSelect
            value={pageSize}
            onChange={e => {
              setPageSize(e.target.value);
              setPage(1);
            }}>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </PageSizeSelect>
        </PageSizeGroup>

        <SearchGroup>
          <SearchInput
            placeholder={t('지점명 검색')}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <SearchButton onClick={handleSearch}>{t('검색')}</SearchButton>
        </SearchGroup>
      </TableControlsRow>

      <Table>
        <TableHead>
          <DataRow>
            {COLUMNS.map(col => (
              <HeaderCell key={col.id} style={{ width: col.width || 'auto' }}>
                {t(col.label)}
              </HeaderCell>
            ))}
          </DataRow>
        </TableHead>
        <TableBody>
          {pagedBranches.length > 0 ? (
            pagedBranches.map((branch, idx) => (
              <DataRow key={branch.branchId || idx}>
                {COLUMNS.map(col => (
                  <BodyCell key={col.id} style={{ width: col.width || 'auto' }}>
                    {renderCellValue(branch, col.id)}
                  </BodyCell>
                ))}
              </DataRow>
            ))
          ) : (
            <DataRow>
              <BodyCell colSpan={COLUMNS.length} style={{ textAlign: 'center', color: '#a6a6a6' }}>
                {t('데이터가 없습니다')}
              </BodyCell>
            </DataRow>
          )}
        </TableBody>
      </Table>

      {renderPagination()}

      {adjustTarget && (
        <SmsBalanceAdjustModal
          open={!!adjustTarget}
          mode={adjustTarget.mode}
          branch={adjustTarget.branch}
          onClose={() => setAdjustTarget(null)}
          onSuccess={handleAdjustSuccess}
        />
      )}
    </>
  );
};

export default SmsBalanceAdjust;
